import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { VarianceLimitsSettings } from '../components/VarianceLimitsSettings';
import { Mail, MessageSquare, Plus, Save, Loader2, Trash2, TestTube, Info, CheckCircle, AlertCircle, Eye, EyeOff, CreditCard, Receipt, ShoppingBag, BadgePoundSterling, Edit2, X, Users, Fuel, Building, Pencil, Trash, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [storeTypes, setStoreTypes] = useState([]);
  const [newStoreType, setNewStoreType] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Payment Types & Paid Out Types
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paidOutTypes, setPaidOutTypes] = useState([]);
  const [newPaymentType, setNewPaymentType] = useState('');
  const [newPaidOutType, setNewPaidOutType] = useState('');

  // Scratch Card Denominations
  const [scratchCardDenominations, setScratchCardDenominations] = useState([]);
  const [newDenomination, setNewDenomination] = useState({ name: '', value: '', pack_size: '' });
  const [scratchCardStoreId, setScratchCardStoreId] = useState(''); // Selected store for scratch card denominations

  // Transfer Types
  const [transferTypes, setTransferTypes] = useState([]);
  const [newTransferType, setNewTransferType] = useState('');

  // Purchase Types
  const [purchaseTypes, setPurchaseTypes] = useState([]);
  const [newPurchaseType, setNewPurchaseType] = useState({ name: '', description: '' });
  const [editingPurchaseType, setEditingPurchaseType] = useState(null);

  // Commission Types
  const [commissionTypes, setCommissionTypes] = useState([]);
  const [newCommissionType, setNewCommissionType] = useState({ name: '', description: '' });
  const [editingCommissionType, setEditingCommissionType] = useState(null);

  // Global Services
  const [globalServices, setGlobalServices] = useState([]);
  const [newService, setNewService] = useState({ name: '', key: '', category: 'services', icon: 'circle' });
  const [editingService, setEditingService] = useState(null);

  // Cashiers
  const [cashiers, setCashiers] = useState([]);
  const [newCashier, setNewCashier] = useState({ 
    cashier_id: '',  // Short Name
    first_name: '', 
    last_name: '',
    mobile: '', 
    email: '', 
    join_date: '', 
    address: '',
    contracted_hours: 0,
    pay_type: 'hourly',
    hourly_rate: 0,
    monthly_salary: 0
  });
  const [editingCashier, setEditingCashier] = useState(null);
  const [cashierStoreId, setCashierStoreId] = useState(''); // Selected store for cashiers
  const [stores, setStores] = useState([]);

  // Fuel Card Types
  const [fuelCardTypes, setFuelCardTypes] = useState([]);
  const [newFuelCardType, setNewFuelCardType] = useState({ 
    name: '', 
    category: 'non_bunkering', 
    settlement_frequency: 'weekly',
    handling_fee_per_litre: 0.50,
    diesel_handling_fee_per_litre: 0.55,
    other_grades_merchant_fee_percent: 3,
    shop_items_merchant_fee_percent: 10
  });
  const [editingFuelCardType, setEditingFuelCardType] = useState(null);

  // Buying Groups
  const [buyingGroups, setBuyingGroups] = useState([]);
  const [newBuyingGroup, setNewBuyingGroup] = useState({ 
    name: '', 
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    payment_terms: '',
    notes: '',
    is_active: true
  });
  const [editingBuyingGroup, setEditingBuyingGroup] = useState(null);

  // Expense Types
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [newExpenseType, setNewExpenseType] = useState({ name: '', description: '' });
  const [editingExpenseType, setEditingExpenseType] = useState(null);

  // Email settings form - Google SMTP
  const [emailForm, setEmailForm] = useState({
    provider: 'google',
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: ''
  });

  // WhatsApp settings form
  const [whatsappForm, setWhatsappForm] = useState({
    api_key: '',
    phone_number_id: '',
    business_account_id: ''
  });

  // Test forms
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');

  // Store Email Configuration
  const [storeEmailStoreId, setStoreEmailStoreId] = useState('');
  const [storeEmailConfig, setStoreEmailConfig] = useState({
    enabled: false,
    smtp_server: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: ''
  });
  const [storeEmailPasswordSet, setStoreEmailPasswordSet] = useState(false);
  const [storeEmailTestEmail, setStoreEmailTestEmail] = useState('');
  const [storeEmailLoading, setStoreEmailLoading] = useState(false);
  const [storeEmailTesting, setStoreEmailTesting] = useState(false);
  const [storeEmailTestPassed, setStoreEmailTestPassed] = useState(false);
  const [showStoreEmailPassword, setShowStoreEmailPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchPaymentTypes();
    fetchPaidOutTypes();
    fetchTransferTypes();
    fetchPurchaseTypes();
    fetchCommissionTypes();
    fetchGlobalServices();
    fetchStores();
    fetchFuelCardTypes();
    fetchBuyingGroups();
    fetchExpenseTypes();
  }, []);

  // Fetch cashiers when store is selected
  useEffect(() => {
    if (cashierStoreId) {
      fetchCashiers(cashierStoreId);
    } else {
      setCashiers([]);
    }
  }, [cashierStoreId]);

  // Fetch scratch card denominations when store is selected
  useEffect(() => {
    if (scratchCardStoreId) {
      fetchScratchCardDenominations(scratchCardStoreId);
    } else {
      setScratchCardDenominations([]);
    }
  }, [scratchCardStoreId]);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data || []);
      // Auto-select first store if available
      if (response.data && response.data.length > 0) {
        setCashierStoreId(response.data[0].id);
        setScratchCardStoreId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [settingsRes, typesRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/settings/store-types`)
      ]);
      setSettings(settingsRes.data);
      setStoreTypes(typesRes.data);

      if (settingsRes.data.email_settings) {
        setEmailForm({
          provider: 'google',
          smtp_server: settingsRes.data.email_settings.smtp_server || 'smtp.gmail.com',
          smtp_port: settingsRes.data.email_settings.smtp_port || 587,
          smtp_username: settingsRes.data.email_settings.smtp_username || '',
          smtp_password: settingsRes.data.email_settings.smtp_password || '',
          from_email: settingsRes.data.email_settings.from_email || ''
        });
      }
      if (settingsRes.data.whatsapp_settings) {
        setWhatsappForm(settingsRes.data.whatsapp_settings);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/payment-types`);
      setPaymentTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch payment types');
    }
  };

  const fetchPaidOutTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/paid-out-types`);
      setPaidOutTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch paid out types');
    }
  };

  const fetchGlobalServices = async () => {
    try {
      const response = await axios.get(`${API}/settings/services`);
      setGlobalServices(response.data || []);
    } catch (error) {
      console.error('Failed to fetch global services');
    }
  };

  const addService = async () => {
    if (!newService.name.trim() || !newService.key.trim()) {
      toast.error('Service name and key are required');
      return;
    }
    try {
      await axios.post(`${API}/settings/services`, newService);
      toast.success('Service added');
      setNewService({ name: '', key: '', category: 'services', icon: 'circle' });
      fetchGlobalServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add service');
    }
  };

  const updateService = async (serviceId, data) => {
    try {
      await axios.put(`${API}/settings/services/${serviceId}`, data);
      toast.success('Service updated');
      setEditingService(null);
      fetchGlobalServices();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/settings/services/${serviceId}`);
      toast.success('Service deleted');
      fetchGlobalServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const addPaymentType = async () => {
    if (!newPaymentType.trim()) return;
    try {
      await axios.post(`${API}/settings/payment-types`, { name: newPaymentType });
      toast.success('Payment type added');
      setNewPaymentType('');
      fetchPaymentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment type');
    }
  };

  const deletePaymentType = async (id) => {
    try {
      await axios.delete(`${API}/settings/payment-types/${id}`);
      toast.success('Payment type deleted');
      fetchPaymentTypes();
    } catch (error) {
      toast.error('Failed to delete payment type');
    }
  };

  const togglePaymentType = async (id, isActive) => {
    try {
      await axios.put(`${API}/settings/payment-types/${id}`, { is_active: !isActive });
      fetchPaymentTypes();
    } catch (error) {
      toast.error('Failed to update payment type');
    }
  };

  const addPaidOutType = async () => {
    if (!newPaidOutType.trim()) return;
    try {
      await axios.post(`${API}/settings/paid-out-types`, { name: newPaidOutType });
      toast.success('Paid out type added');
      setNewPaidOutType('');
      fetchPaidOutTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add paid out type');
    }
  };

  const deletePaidOutType = async (id) => {
    try {
      await axios.delete(`${API}/settings/paid-out-types/${id}`);
      toast.success('Paid out type deleted');
      fetchPaidOutTypes();
    } catch (error) {
      toast.error('Failed to delete paid out type');
    }
  };

  const togglePaidOutType = async (id, isActive) => {
    try {
      await axios.put(`${API}/settings/paid-out-types/${id}`, { is_active: !isActive });
      fetchPaidOutTypes();
    } catch (error) {
      toast.error('Failed to update paid out type');
    }
  };

  // Scratch Card Denominations functions
  const fetchScratchCardDenominations = async (storeId = scratchCardStoreId) => {
    if (!storeId) return;
    try {
      const response = await axios.get(`${API}/settings/scratch-card-denominations`, {
        params: { store_id: storeId }
      });
      setScratchCardDenominations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch scratch card denominations');
    }
  };

  const addScratchCardDenomination = async () => {
    if (!newDenomination.name.trim() || !newDenomination.value || !scratchCardStoreId) return;
    try {
      await axios.post(`${API}/settings/scratch-card-denominations`, {
        name: newDenomination.name,
        value: parseFloat(newDenomination.value),
        pack_size: parseInt(newDenomination.pack_size) || 100,
        store_id: scratchCardStoreId
      });
      toast.success('Denomination added');
      setNewDenomination({ name: '', value: '', pack_size: '' });
      fetchScratchCardDenominations(scratchCardStoreId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add denomination');
    }
  };

  const deleteScratchCardDenomination = async (id) => {
    try {
      await axios.delete(`${API}/settings/scratch-card-denominations/${id}`);
      toast.success('Denomination deleted');
      fetchScratchCardDenominations(scratchCardStoreId);
    } catch (error) {
      toast.error('Failed to delete denomination');
    }
  };

  const toggleScratchCardDenomination = async (id, isActive) => {
    try {
      await axios.put(`${API}/settings/scratch-card-denominations/${id}`, { is_active: !isActive });
      fetchScratchCardDenominations(scratchCardStoreId);
    } catch (error) {
      toast.error('Failed to update denomination');
    }
  };

  const updateScratchCardDenomination = async (id, field, value) => {
    try {
      const updateData = { [field]: field === 'value' || field === 'pack_size' ? parseFloat(value) || 0 : value };
      await axios.put(`${API}/settings/scratch-card-denominations/${id}`, updateData);
      fetchScratchCardDenominations(scratchCardStoreId);
      toast.success('Denomination updated');
    } catch (error) {
      toast.error('Failed to update denomination');
    }
  };

  // Transfer Types functions
  const fetchTransferTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/transfer-types`);
      setTransferTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch transfer types');
    }
  };

  const addTransferType = async () => {
    if (!newTransferType.trim()) return;
    try {
      await axios.post(`${API}/settings/transfer-types`, { name: newTransferType });
      toast.success('Transfer type added');
      setNewTransferType('');
      fetchTransferTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add transfer type');
    }
  };

  const deleteTransferType = async (id) => {
    try {
      await axios.delete(`${API}/settings/transfer-types/${id}`);
      toast.success('Transfer type deleted');
      fetchTransferTypes();
    } catch (error) {
      toast.error('Failed to delete transfer type');
    }
  };

  const toggleTransferType = async (id, isActive) => {
    try {
      await axios.put(`${API}/settings/transfer-types/${id}`, { is_active: !isActive });
      fetchTransferTypes();
    } catch (error) {
      toast.error('Failed to update transfer type');
    }
  };

  // Purchase Types functions
  const fetchPurchaseTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/purchase-types`);
      setPurchaseTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch purchase types');
    }
  };

  const addPurchaseType = async () => {
    if (!newPurchaseType.name.trim()) return;
    try {
      await axios.post(`${API}/settings/purchase-types`, newPurchaseType);
      toast.success('Purchase type added');
      setNewPurchaseType({ name: '', description: '' });
      fetchPurchaseTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add purchase type');
    }
  };

  const updatePurchaseType = async (id) => {
    if (!editingPurchaseType) return;
    try {
      await axios.put(`${API}/settings/purchase-types/${id}`, editingPurchaseType);
      toast.success('Purchase type updated');
      setEditingPurchaseType(null);
      fetchPurchaseTypes();
    } catch (error) {
      toast.error('Failed to update purchase type');
    }
  };

  const deletePurchaseType = async (id) => {
    try {
      await axios.delete(`${API}/settings/purchase-types/${id}`);
      toast.success('Purchase type deleted');
      fetchPurchaseTypes();
    } catch (error) {
      toast.error('Failed to delete purchase type');
    }
  };

  const togglePurchaseType = async (id, isActive) => {
    try {
      await axios.put(`${API}/settings/purchase-types/${id}`, { is_active: !isActive });
      fetchPurchaseTypes();
    } catch (error) {
      toast.error('Failed to update purchase type');
    }
  };

  // Commission Types functions
  const fetchCommissionTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/commission-types`);
      setCommissionTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch commission types');
    }
  };

  const addCommissionType = async () => {
    if (!newCommissionType.name.trim()) return;
    try {
      await axios.post(`${API}/settings/commission-types`, newCommissionType);
      toast.success('Commission type added');
      setNewCommissionType({ name: '', description: '' });
      fetchCommissionTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add commission type');
    }
  };

  const updateCommissionType = async (id) => {
    if (!editingCommissionType) return;
    try {
      await axios.put(`${API}/settings/commission-types/${id}`, editingCommissionType);
      toast.success('Commission type updated');
      setEditingCommissionType(null);
      fetchCommissionTypes();
    } catch (error) {
      toast.error('Failed to update commission type');
    }
  };

  const deleteCommissionType = async (id) => {
    try {
      await axios.delete(`${API}/settings/commission-types/${id}`);
      toast.success('Commission type deleted');
      fetchCommissionTypes();
    } catch (error) {
      toast.error('Failed to delete commission type');
    }
  };

  const toggleCommissionType = async (id, isActive) => {
    try {
      await axios.put(`${API}/settings/commission-types/${id}`, { is_active: !isActive });
      fetchCommissionTypes();
    } catch (error) {
      toast.error('Failed to update commission type');
    }
  };

  // Expense Types functions
  const fetchExpenseTypes = async () => {
    try {
      const response = await axios.get(`${API}/expenses/types`);
      setExpenseTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch expense types');
    }
  };

  const addExpenseType = async () => {
    if (!newExpenseType.name.trim()) return;
    try {
      await axios.post(`${API}/expenses/types`, newExpenseType);
      toast.success('Expense type added');
      setNewExpenseType({ name: '', description: '' });
      fetchExpenseTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense type');
    }
  };

  const updateExpenseType = async (id) => {
    if (!editingExpenseType) return;
    try {
      await axios.put(`${API}/expenses/types/${id}`, editingExpenseType);
      toast.success('Expense type updated');
      setEditingExpenseType(null);
      fetchExpenseTypes();
    } catch (error) {
      toast.error('Failed to update expense type');
    }
  };

  const deleteExpenseType = async (id) => {
    try {
      await axios.delete(`${API}/expenses/types/${id}`);
      toast.success('Expense type deleted');
      fetchExpenseTypes();
    } catch (error) {
      toast.error('Failed to delete expense type');
    }
  };

  const toggleExpenseType = async (id, isActive) => {
    try {
      await axios.put(`${API}/expenses/types/${id}`, { is_active: !isActive });
      fetchExpenseTypes();
    } catch (error) {
      toast.error('Failed to update expense type');
    }
  };

  const seedDefaultExpenseTypes = async () => {
    try {
      const response = await axios.post(`${API}/expenses/seed-types`);
      toast.success(response.data?.message || 'Default expense types seeded');
      fetchExpenseTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed expense types');
    }
  };

  // Store Email Configuration functions
  const fetchStoreEmailConfig = async (storeId) => {
    if (!storeId) return;
    setStoreEmailLoading(true);
    setStoreEmailTestPassed(false);
    try {
      const response = await axios.get(`${API}/stores/${storeId}/email-config`);
      const config = response.data?.email_config || {};
      setStoreEmailConfig({
        enabled: config.enabled || false,
        smtp_server: config.smtp_server || '',
        smtp_port: config.smtp_port || 587,
        smtp_username: config.smtp_username || '',
        smtp_password: config.smtp_password || '',
        from_email: config.from_email || '',
        from_name: config.from_name || ''
      });
      setStoreEmailPasswordSet(config.smtp_password_set || false);
    } catch (error) {
      console.error('Failed to fetch store email config:', error);
      setStoreEmailConfig({
        enabled: false,
        smtp_server: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        from_email: '',
        from_name: ''
      });
      setStoreEmailPasswordSet(false);
    } finally {
      setStoreEmailLoading(false);
    }
  };

  const testStoreEmailConfig = async () => {
    if (!storeEmailStoreId) {
      toast.error('Please select a store first');
      return;
    }
    if (!storeEmailTestEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    if (!storeEmailConfig.smtp_server || !storeEmailConfig.smtp_username) {
      toast.error('Please fill in SMTP server and username');
      return;
    }
    // Check if password is set (either new or existing)
    if (!storeEmailConfig.smtp_password && !storeEmailPasswordSet) {
      toast.error('Please enter SMTP password');
      return;
    }
    
    setStoreEmailTesting(true);
    setStoreEmailTestPassed(false);
    try {
      await axios.post(`${API}/stores/${storeEmailStoreId}/email-config/test`, {
        test_email: storeEmailTestEmail,
        smtp_server: storeEmailConfig.smtp_server,
        smtp_port: storeEmailConfig.smtp_port,
        smtp_username: storeEmailConfig.smtp_username,
        smtp_password: storeEmailConfig.smtp_password,
        from_email: storeEmailConfig.from_email,
        from_name: storeEmailConfig.from_name
      });
      toast.success('Test email sent successfully! Check your inbox.');
      setStoreEmailTestPassed(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
      setStoreEmailTestPassed(false);
    } finally {
      setStoreEmailTesting(false);
    }
  };

  const saveStoreEmailConfig = async () => {
    if (!storeEmailStoreId) {
      toast.error('Please select a store first');
      return;
    }
    if (!storeEmailTestPassed && storeEmailConfig.enabled) {
      toast.error('Please send a successful test email before saving');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/stores/${storeEmailStoreId}/email-config`, storeEmailConfig);
      toast.success('Store email configuration saved');
      setStoreEmailTestPassed(false); // Reset for next change
      fetchStoreEmailConfig(storeEmailStoreId); // Refresh to get masked password
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const clearStoreEmailConfig = async () => {
    if (!storeEmailStoreId) return;
    
    if (!window.confirm('Are you sure you want to disable and clear the email configuration for this store? It will fall back to global email settings.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/stores/${storeEmailStoreId}/email-config`);
      toast.success('Store email configuration cleared');
      fetchStoreEmailConfig(storeEmailStoreId);
    } catch (error) {
      toast.error('Failed to clear email configuration');
    }
  };

  // Cashiers functions
  const fetchCashiers = async (storeId) => {
    try {
      const response = await axios.get(`${API}/settings/cashiers?store_id=${storeId}`);
      setCashiers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch cashiers:', error);
    }
  };

  const addCashier = async () => {
    if (!newCashier.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!cashierStoreId) {
      toast.error('Please select a store first');
      return;
    }
    try {
      await axios.post(`${API}/settings/cashiers`, {
        ...newCashier,
        store_id: cashierStoreId
      });
      setNewCashier({ 
        cashier_id: '', 
        first_name: '', 
        last_name: '',
        mobile: '', 
        email: '', 
        join_date: '', 
        address: '',
        contracted_hours: 0,
        pay_type: 'hourly',
        hourly_rate: 0,
        monthly_salary: 0
      });
      fetchCashiers(cashierStoreId);
      toast.success('Cashier added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add cashier');
    }
  };

  const updateCashier = async (id) => {
    // Support legacy records that have 'name' instead of 'first_name'
    const firstName = editingCashier?.first_name || editingCashier?.name || '';
    if (!firstName.trim()) {
      toast.error('First name is required');
      return;
    }
    try {
      await axios.put(`${API}/settings/cashiers/${id}`, {
        cashier_id: editingCashier.cashier_id,
        first_name: firstName.trim(),
        last_name: editingCashier.last_name || '',
        mobile: editingCashier.mobile,
        email: editingCashier.email,
        join_date: editingCashier.join_date,
        address: editingCashier.address,
        contracted_hours: editingCashier.contracted_hours,
        pay_type: editingCashier.pay_type || 'hourly',
        hourly_rate: editingCashier.hourly_rate || 0,
        monthly_salary: editingCashier.monthly_salary || 0
      });
      setEditingCashier(null);
      fetchCashiers(cashierStoreId);
      toast.success('Cashier updated');
    } catch (error) {
      toast.error('Failed to update cashier');
    }
  };

  const deleteCashier = async (id) => {
    if (!window.confirm('Delete this cashier?')) return;
    try {
      await axios.delete(`${API}/settings/cashiers/${id}`);
      fetchCashiers(cashierStoreId);
      toast.success('Cashier deleted');
    } catch (error) {
      toast.error('Failed to delete cashier');
    }
  };

  const toggleCashier = async (id, isActive) => {
    try {
      await axios.put(`${API}/settings/cashiers/${id}`, { is_active: !isActive });
      fetchCashiers(cashierStoreId);
    } catch (error) {
      toast.error('Failed to update cashier');
    }
  };

  // Fuel Card Types functions
  const fetchFuelCardTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/fuel-card-types`);
      setFuelCardTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch fuel card types:', error);
    }
  };

  const addFuelCardType = async () => {
    if (!newFuelCardType.name.trim()) {
      toast.error('Card name is required');
      return;
    }
    try {
      await axios.post(`${API}/settings/fuel-card-types`, newFuelCardType);
      setNewFuelCardType({ 
        name: '', 
        category: 'non_bunkering', 
        settlement_frequency: 'weekly',
        handling_fee_per_litre: 0.50,
        diesel_handling_fee_per_litre: 0.55,
        other_grades_merchant_fee_percent: 3,
        shop_items_merchant_fee_percent: 10
      });
      fetchFuelCardTypes();
      toast.success('Fuel card type added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add fuel card type');
    }
  };

  const updateFuelCardType = async (id) => {
    if (!editingFuelCardType?.name.trim()) {
      toast.error('Card name is required');
      return;
    }
    try {
      await axios.put(`${API}/settings/fuel-card-types/${id}`, editingFuelCardType);
      setEditingFuelCardType(null);
      fetchFuelCardTypes();
      toast.success('Fuel card type updated');
    } catch (error) {
      toast.error('Failed to update fuel card type');
    }
  };

  const deleteFuelCardType = async (id) => {
    if (!window.confirm('Delete this fuel card type?')) return;
    try {
      await axios.delete(`${API}/settings/fuel-card-types/${id}`);
      fetchFuelCardTypes();
      toast.success('Fuel card type deleted');
    } catch (error) {
      toast.error('Failed to delete fuel card type');
    }
  };

  const toggleFuelCardType = async (id, isActive) => {
    try {
      await axios.put(`${API}/settings/fuel-card-types/${id}`, { is_active: !isActive });
      fetchFuelCardTypes();
    } catch (error) {
      toast.error('Failed to toggle fuel card type');
    }
  };

  // Buying Groups functions
  const fetchBuyingGroups = async () => {
    try {
      const response = await axios.get(`${API}/settings/buying-groups`);
      setBuyingGroups(response.data || []);
    } catch (error) {
      console.error('Failed to fetch buying groups:', error);
    }
  };

  const addBuyingGroup = async () => {
    if (!newBuyingGroup.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      await axios.post(`${API}/settings/buying-groups`, newBuyingGroup);
      setNewBuyingGroup({ 
        name: '', 
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        payment_terms: '',
        notes: '',
        is_active: true
      });
      fetchBuyingGroups();
      toast.success('Buying group added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add buying group');
    }
  };

  const updateBuyingGroup = async (id) => {
    if (!editingBuyingGroup?.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      await axios.put(`${API}/settings/buying-groups/${id}`, editingBuyingGroup);
      setEditingBuyingGroup(null);
      fetchBuyingGroups();
      toast.success('Buying group updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update buying group');
    }
  };

  const deleteBuyingGroup = async (id) => {
    if (!window.confirm('Delete this buying group?')) return;
    try {
      await axios.delete(`${API}/settings/buying-groups/${id}`);
      fetchBuyingGroups();
      toast.success('Buying group deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete buying group');
    }
  };

  const toggleBuyingGroup = async (id, isActive) => {
    try {
      await axios.put(`${API}/settings/buying-groups/${id}`, { is_active: !isActive });
      fetchBuyingGroups();
    } catch (error) {
      toast.error('Failed to toggle buying group');
    }
  };

  const saveEmailSettings = async () => {
    if (!emailForm.smtp_username || !emailForm.smtp_password) {
      toast.error('Gmail address and App Password are required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/settings/email`, {
        ...emailForm,
        from_email: emailForm.from_email || emailForm.smtp_username
      });
      toast.success('Email settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const saveWhatsAppSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/whatsapp`, whatsappForm);
      toast.success('WhatsApp settings saved');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save WhatsApp settings');
    } finally {
      setSaving(false);
    }
  };

  const testEmailConfig = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    if (!settings?.email_settings?.is_configured) {
      toast.error('Please save email settings first');
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/settings/test-email`, { email: testEmail });
      if (res.data.status === 'sent') {
        toast.success('Test email sent successfully! Check your inbox.');
      } else {
        toast.info(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const testWhatsAppConfig = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    try {
      const res = await axios.post(`${API}/settings/test-whatsapp`, { phone_number: testPhone });
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to send test message');
    }
  };

  const addStoreType = async () => {
    if (!newStoreType.trim()) return;
    try {
      await axios.post(`${API}/settings/store-types`, { store_type: newStoreType.toLowerCase().replace(/\s+/g, '_') });
      toast.success('Store type added');
      setNewStoreType('');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add store type');
    }
  };

  const removeStoreType = async (type) => {
    try {
      await axios.delete(`${API}/settings/store-types/${type}`);
      toast.success('Store type removed');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to remove store type');
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure email, WhatsApp, and system settings
        </p>
      </div>

      <Tabs defaultValue="email">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="email">Email</TabsTrigger>
          <TabsTrigger value="store-email">Store Email</TabsTrigger>
          <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="fuel-cards">Fuel Cards</TabsTrigger>
          <TabsTrigger value="buying-groups">Buying Groups</TabsTrigger>
          <TabsTrigger value="payment-types">Payment Types</TabsTrigger>
          <TabsTrigger value="paid-out-types">Paid Out Types</TabsTrigger>
          <TabsTrigger value="purchase-types">Purchase Types</TabsTrigger>
          <TabsTrigger value="commission-types">Commission Types</TabsTrigger>
          <TabsTrigger value="expense-types">Expense Types</TabsTrigger>
          <TabsTrigger value="cashiers">Cashiers</TabsTrigger>
          <TabsTrigger value="scratch-cards">Scratch Cards</TabsTrigger>
          <TabsTrigger value="transfer-types">Transfer Types</TabsTrigger>
          <TabsTrigger value="store-types">Store Types</TabsTrigger>
          <TabsTrigger value="variance-limits" data-testid="variance-limits-tab">Variance Limits</TabsTrigger>
        </TabsList>

        <TabsContent value="variance-limits" className="mt-6">
          <VarianceLimitsSettings stores={stores} />
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Mail className="h-5 w-5 text-primary" />
                Google SMTP Configuration
              </CardTitle>
              <CardDescription>
                Configure Gmail to send password reset emails and notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status and Instructions */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                {settings?.email_settings?.is_configured ? (
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Email is configured and ready</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-400">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Email not configured</span>
                  </div>
                )}
                <div className="text-sm text-muted-foreground space-y-2">
                  <p className="flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      To use Gmail SMTP, you need to generate an <strong>App Password</strong> from your Google Account.
                    </span>
                  </p>
                  <ol className="list-decimal ml-8 space-y-1">
                    <li>Go to your Google Account → Security</li>
                    <li>Enable 2-Step Verification if not already enabled</li>
                    <li>Search for "App passwords" or go to Security → App passwords</li>
                    <li>Create a new app password for "Mail" and "Other (Custom name)"</li>
                    <li>Copy the 16-character password and paste it below</li>
                  </ol>
                </div>
              </div>

              {/* SMTP Form */}
              <div className="grid gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp_server">SMTP Server</Label>
                    <Input
                      id="smtp_server"
                      value={emailForm.smtp_server}
                      onChange={(e) => setEmailForm({ ...emailForm, smtp_server: e.target.value })}
                      placeholder="smtp.gmail.com"
                      data-testid="smtp-server-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_port">SMTP Port</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      value={emailForm.smtp_port}
                      onChange={(e) => setEmailForm({ ...emailForm, smtp_port: parseInt(e.target.value) || 587 })}
                      placeholder="587"
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp_username">Gmail Address</Label>
                  <Input
                    id="smtp_username"
                    type="email"
                    value={emailForm.smtp_username}
                    onChange={(e) => setEmailForm({ ...emailForm, smtp_username: e.target.value })}
                    placeholder="your-email@gmail.com"
                    data-testid="smtp-username-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp_password">App Password</Label>
                  <div className="relative">
                    <Input
                      id="smtp_password"
                      type={showPassword ? 'text' : 'password'}
                      value={emailForm.smtp_password}
                      onChange={(e) => setEmailForm({ ...emailForm, smtp_password: e.target.value })}
                      placeholder="16-character app password"
                      className="pr-10"
                      data-testid="smtp-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This is NOT your regular Gmail password. Use the App Password generated from Google Account settings.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="from_email">From Email (Optional)</Label>
                  <Input
                    id="from_email"
                    type="email"
                    value={emailForm.from_email}
                    onChange={(e) => setEmailForm({ ...emailForm, from_email: e.target.value })}
                    placeholder="Leave blank to use Gmail address"
                    data-testid="from-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Display name for sent emails. If empty, your Gmail address will be used.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Button onClick={saveEmailSettings} disabled={saving} data-testid="save-email-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Email Settings
                </Button>
              </div>

              {/* Test Email Section */}
              <div className="border-t border-border pt-6">
                <h4 className="font-medium mb-4">Test Email Configuration</h4>
                <div className="flex gap-4">
                  <Input
                    type="email"
                    placeholder="Enter email to send test"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="max-w-xs"
                    data-testid="test-email-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={testEmailConfig} 
                    disabled={testing || !settings?.email_settings?.is_configured}
                    data-testid="test-email-btn"
                  >
                    {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
                    Send Test Email
                  </Button>
                </div>
                {!settings?.email_settings?.is_configured && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Save your email settings first before testing
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Store Email Settings */}
        <TabsContent value="store-email" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Building className="h-5 w-5 text-blue-500" />
                Store Email Configuration
              </CardTitle>
              <CardDescription>
                Configure individual email settings for each store. When enabled, notifications from this store will be sent using its own SMTP configuration.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Store Selection */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-muted-foreground mb-3">
                  Select a store to configure its email settings. Stores without custom email configuration will use the global email settings.
                </p>
                <div className="space-y-2">
                  <Label>Select Store</Label>
                  <Select
                    value={storeEmailStoreId}
                    onValueChange={(value) => {
                      setStoreEmailStoreId(value);
                      setStoreEmailTestPassed(false);
                      fetchStoreEmailConfig(value);
                    }}
                  >
                    <SelectTrigger data-testid="store-email-store-select">
                      <SelectValue placeholder="Select a store..." />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map(store => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {storeEmailStoreId && (
                <>
                  {storeEmailLoading ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="ml-2">Loading configuration...</span>
                    </div>
                  ) : (
                    <>
                      {/* Enable Toggle */}
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div className="space-y-0.5">
                          <Label className="text-base">Enable Store-Specific Email</Label>
                          <p className="text-sm text-muted-foreground">
                            {storeEmailConfig.enabled 
                              ? 'This store uses its own email configuration' 
                              : 'This store uses global email settings'}
                          </p>
                        </div>
                        <Switch
                          checked={storeEmailConfig.enabled}
                          onCheckedChange={(checked) => {
                            setStoreEmailConfig(prev => ({ ...prev, enabled: checked }));
                            setStoreEmailTestPassed(false);
                          }}
                          data-testid="store-email-enabled-toggle"
                        />
                      </div>

                      {/* SMTP Configuration Form */}
                      <div className={`space-y-4 ${!storeEmailConfig.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>SMTP Server</Label>
                            <Input
                              placeholder="smtp.gmail.com"
                              value={storeEmailConfig.smtp_server}
                              onChange={(e) => {
                                setStoreEmailConfig(prev => ({ ...prev, smtp_server: e.target.value }));
                                setStoreEmailTestPassed(false);
                              }}
                              data-testid="store-email-smtp-server"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>SMTP Port</Label>
                            <Input
                              type="number"
                              placeholder="587"
                              value={storeEmailConfig.smtp_port}
                              onChange={(e) => {
                                setStoreEmailConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }));
                                setStoreEmailTestPassed(false);
                              }}
                              data-testid="store-email-smtp-port"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>SMTP Username (Email)</Label>
                            <Input
                              type="email"
                              placeholder="store@yourdomain.com"
                              value={storeEmailConfig.smtp_username}
                              onChange={(e) => {
                                setStoreEmailConfig(prev => ({ ...prev, smtp_username: e.target.value }));
                                setStoreEmailTestPassed(false);
                              }}
                              data-testid="store-email-smtp-username"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>SMTP Password {storeEmailPasswordSet && storeEmailConfig.smtp_password === '••••••••' && <span className="text-green-500 text-xs">(saved)</span>}</Label>
                            <div className="relative">
                              <Input
                                type={showStoreEmailPassword ? 'text' : 'password'}
                                placeholder={storeEmailPasswordSet ? '••••••••' : 'App password or SMTP password'}
                                value={storeEmailConfig.smtp_password}
                                onChange={(e) => {
                                  setStoreEmailConfig(prev => ({ ...prev, smtp_password: e.target.value }));
                                  setStoreEmailTestPassed(false);
                                }}
                                data-testid="store-email-smtp-password"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-full px-3"
                                onClick={() => setShowStoreEmailPassword(!showStoreEmailPassword)}
                              >
                                {showStoreEmailPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>From Email</Label>
                            <Input
                              type="email"
                              placeholder="Same as SMTP username if empty"
                              value={storeEmailConfig.from_email}
                              onChange={(e) => {
                                setStoreEmailConfig(prev => ({ ...prev, from_email: e.target.value }));
                                setStoreEmailTestPassed(false);
                              }}
                              data-testid="store-email-from-email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>From Name (Display Name)</Label>
                            <Input
                              placeholder="Store Name"
                              value={storeEmailConfig.from_name}
                              onChange={(e) => {
                                setStoreEmailConfig(prev => ({ ...prev, from_name: e.target.value }));
                                setStoreEmailTestPassed(false);
                              }}
                              data-testid="store-email-from-name"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Test Email Section */}
                      {storeEmailConfig.enabled && (
                        <div className="p-4 border rounded-lg space-y-4">
                          <div className="flex items-center gap-2">
                            <TestTube className="h-5 w-5 text-yellow-500" />
                            <Label className="text-base">Test Configuration</Label>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Send a test email to verify your SMTP settings. A successful test is required before saving.
                          </p>
                          <div className="flex gap-2">
                            <Input
                              type="email"
                              placeholder="test@example.com"
                              value={storeEmailTestEmail}
                              onChange={(e) => setStoreEmailTestEmail(e.target.value)}
                              className="flex-1"
                              data-testid="store-email-test-address"
                            />
                            <Button
                              variant="outline"
                              onClick={testStoreEmailConfig}
                              disabled={storeEmailTesting}
                              data-testid="store-email-test-button"
                            >
                              {storeEmailTesting ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <TestTube className="mr-2 h-4 w-4" />
                                  Send Test
                                </>
                              )}
                            </Button>
                          </div>
                          {storeEmailTestPassed && (
                            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-500">
                              <CheckCircle className="h-5 w-5" />
                              <span>Test passed! You can now save the configuration.</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-4 border-t">
                        <Button
                          onClick={saveStoreEmailConfig}
                          disabled={saving || (storeEmailConfig.enabled && !storeEmailTestPassed)}
                          data-testid="store-email-save-button"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="mr-2 h-4 w-4" />
                              Save Configuration
                            </>
                          )}
                        </Button>
                        {(storeEmailConfig.enabled || storeEmailPasswordSet) && (
                          <Button
                            variant="outline"
                            onClick={clearStoreEmailConfig}
                            className="text-red-500 hover:text-red-600"
                            data-testid="store-email-clear-button"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Clear & Use Global
                          </Button>
                        )}
                      </div>

                      {/* Info Box */}
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p><strong>Note:</strong> When store-specific email is enabled:</p>
                            <ul className="list-disc list-inside ml-2 space-y-1">
                              <li>Variance notifications will be sent FROM this store's email</li>
                              <li>Reports and alerts will show this store's identity</li>
                              <li>Password is stored securely and masked after saving</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {!storeEmailStoreId && (
                <div className="flex items-center justify-center p-8 text-muted-foreground">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Select a store above to configure its email settings
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Settings */}
        <TabsContent value="whatsapp" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <MessageSquare className="h-5 w-5 text-green-500" />
                WhatsApp Business Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Configure WhatsApp Business API to enable sending reports via WhatsApp.
                  You'll need a WhatsApp Business account and access to the Meta Developer Portal.
                </p>
                {settings?.whatsapp_settings?.is_configured ? (
                  <p className="text-sm text-green-400 mt-2">✓ WhatsApp is configured</p>
                ) : (
                  <p className="text-sm text-yellow-400 mt-2">⚠ WhatsApp not configured</p>
                )}
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>API Key / Access Token</Label>
                  <Input
                    type="password"
                    value={whatsappForm.api_key || ''}
                    onChange={(e) => setWhatsappForm({ ...whatsappForm, api_key: e.target.value })}
                    placeholder="WhatsApp Business API Access Token"
                    data-testid="whatsapp-api-key"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone Number ID</Label>
                    <Input
                      value={whatsappForm.phone_number_id || ''}
                      onChange={(e) => setWhatsappForm({ ...whatsappForm, phone_number_id: e.target.value })}
                      placeholder="WhatsApp Phone Number ID"
                      data-testid="whatsapp-phone-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Business Account ID</Label>
                    <Input
                      value={whatsappForm.business_account_id || ''}
                      onChange={(e) => setWhatsappForm({ ...whatsappForm, business_account_id: e.target.value })}
                      placeholder="WhatsApp Business Account ID"
                      data-testid="whatsapp-account-id"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button onClick={saveWhatsAppSettings} disabled={saving} data-testid="save-whatsapp-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save WhatsApp Settings
                </Button>
              </div>

              <div className="border-t border-border pt-6">
                <h4 className="font-medium mb-4">Test WhatsApp Configuration</h4>
                <div className="flex gap-4">
                  <Input
                    placeholder="Phone number (e.g., +447123456789)"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="max-w-xs"
                    data-testid="test-phone-input"
                  />
                  <Button variant="outline" onClick={testWhatsAppConfig} data-testid="test-whatsapp-btn">
                    <TestTube className="h-4 w-4 mr-2" />
                    Send Test Message
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Store Types */}
        <TabsContent value="store-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Store Types</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Manage the available store types that can be used when creating new stores.
                </p>
              </div>

              <div className="space-y-3">
                {storeTypes.map((type) => (
                  <div key={type} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="capitalize">{type.replace('_', ' ')}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeStoreType(type)}
                      className="text-destructive"
                      data-testid={`remove-type-${type}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Input
                  placeholder="New store type name"
                  value={newStoreType}
                  onChange={(e) => setNewStoreType(e.target.value)}
                  className="max-w-xs"
                  data-testid="new-store-type-input"
                />
                <Button variant="outline" onClick={addStoreType} data-testid="add-store-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Type
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Types */}
        {/* Global Services */}
        <TabsContent value="services" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <ShoppingBag className="h-5 w-5 text-primary" />
                Global Services
              </CardTitle>
              <CardDescription>
                Manage services available for all stores. These will be available when setting up stores.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Define global services like Lottery, Scratch Cards, PayPoint, Payzone, etc. Stores can enable/disable these as needed.
                </p>
              </div>

              {/* Services by Category */}
              {['lottery', 'services', 'fuel_services'].map(category => {
                const categoryServices = globalServices.filter(s => s.category === category);
                if (categoryServices.length === 0) return null;
                return (
                  <div key={category} className="space-y-2">
                    <h4 className="font-semibold text-sm uppercase text-muted-foreground">
                      {category === 'lottery' ? 'Lottery & Scratch Cards' : 
                       category === 'services' ? 'Payment Services' : 'Fuel Station Services'}
                    </h4>
                    <div className="space-y-2">
                      {categoryServices.map((service) => (
                        <div key={service.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          {editingService?.id === service.id ? (
                            <div className="flex items-center gap-2 flex-1">
                              <Input
                                value={editingService.name}
                                onChange={(e) => setEditingService({...editingService, name: e.target.value})}
                                className="max-w-[150px]"
                              />
                              <Input
                                value={editingService.key}
                                onChange={(e) => setEditingService({...editingService, key: e.target.value})}
                                className="max-w-[120px]"
                                placeholder="key"
                              />
                              <Select
                                value={editingService.category}
                                onValueChange={(v) => setEditingService({...editingService, category: v})}
                              >
                                <SelectTrigger className="w-[140px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="lottery">Lottery</SelectItem>
                                  <SelectItem value="services">Services</SelectItem>
                                  <SelectItem value="fuel_services">Fuel Services</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button size="sm" onClick={() => updateService(service.id, editingService)}>
                                <Save className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingService(null)}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-3">
                                <Switch
                                  checked={service.is_active}
                                  onCheckedChange={() => updateService(service.id, { is_active: !service.is_active })}
                                />
                                <div>
                                  <span className={service.is_active ? 'font-medium' : 'text-muted-foreground'}>{service.name}</span>
                                  <span className="text-xs text-muted-foreground ml-2">({service.key})</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setEditingService(service)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => deleteService(service.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Add New Service */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Add New Service</h4>
                <div className="flex gap-2 flex-wrap">
                  <Input
                    placeholder="Service Name"
                    value={newService.name}
                    onChange={(e) => setNewService({...newService, name: e.target.value, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                    className="max-w-[150px]"
                    data-testid="new-service-name"
                  />
                  <Input
                    placeholder="Key (auto)"
                    value={newService.key}
                    onChange={(e) => setNewService({...newService, key: e.target.value})}
                    className="max-w-[120px]"
                    data-testid="new-service-key"
                  />
                  <Select
                    value={newService.category}
                    onValueChange={(v) => setNewService({...newService, category: v})}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lottery">Lottery</SelectItem>
                      <SelectItem value="services">Services</SelectItem>
                      <SelectItem value="fuel_services">Fuel Services</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={addService} data-testid="add-service-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Service
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fuel Card Types */}
        <TabsContent value="fuel-cards" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Fuel className="h-5 w-5 text-primary" />
                Fuel Card Types
              </CardTitle>
              <CardDescription>
                Manage fuel card types for fuel stations. Enable/disable cards that your stores accept.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Non Bunkering Cards Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-blue-500">
                  <CreditCard className="h-5 w-5" />
                  Non Bunkering Cards
                  <span className="text-xs text-muted-foreground font-normal">(Paid at retail price - Weekly settlement)</span>
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Card Name</th>
                        <th className="text-left p-3 text-sm font-medium">Settlement</th>
                        <th className="text-center p-3 text-sm font-medium">Status</th>
                        <th className="text-right p-3 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {fuelCardTypes.filter(fc => fc.category === 'non_bunkering').map((fc) => (
                        <tr key={fc.id} className={!fc.is_active ? 'bg-muted/30' : ''}>
                          <td className="p-3">
                            {editingFuelCardType?.id === fc.id ? (
                              <Input 
                                value={editingFuelCardType.name}
                                onChange={(e) => setEditingFuelCardType({...editingFuelCardType, name: e.target.value})}
                                className="h-8"
                              />
                            ) : (
                              <span className="font-medium">{fc.name}</span>
                            )}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground capitalize">{fc.settlement_frequency}</td>
                          <td className="p-3 text-center">
                            <Switch 
                              checked={fc.is_active} 
                              onCheckedChange={() => toggleFuelCardType(fc.id, fc.is_active)}
                            />
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-2">
                              {editingFuelCardType?.id === fc.id ? (
                                <>
                                  <Button size="sm" onClick={() => updateFuelCardType(fc.id)}>
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingFuelCardType(null)}>
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingFuelCardType(fc)}>
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteFuelCardType(fc.id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bunkering Cards Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-orange-500">
                  <Fuel className="h-5 w-5" />
                  Bunkering Cards
                  <span className="text-xs text-muted-foreground font-normal">(Cost price + Handling fee - Weekly settlement)</span>
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Card Name</th>
                        <th className="text-left p-3 text-sm font-medium">Handling Fee</th>
                        <th className="text-left p-3 text-sm font-medium">Settlement</th>
                        <th className="text-center p-3 text-sm font-medium">Status</th>
                        <th className="text-right p-3 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {fuelCardTypes.filter(fc => fc.category === 'bunkering').map((fc) => (
                        <tr key={fc.id} className={!fc.is_active ? 'bg-muted/30' : ''}>
                          <td className="p-3">
                            {editingFuelCardType?.id === fc.id ? (
                              <Input 
                                value={editingFuelCardType.name}
                                onChange={(e) => setEditingFuelCardType({...editingFuelCardType, name: e.target.value})}
                                className="h-8"
                              />
                            ) : (
                              <span className="font-medium">{fc.name}</span>
                            )}
                          </td>
                          <td className="p-3">
                            {editingFuelCardType?.id === fc.id ? (
                              <div className="flex items-center gap-1">
                                <Input 
                                  type="number"
                                  step="0.01"
                                  value={editingFuelCardType.handling_fee_per_litre}
                                  onChange={(e) => setEditingFuelCardType({...editingFuelCardType, handling_fee_per_litre: parseFloat(e.target.value) || 0})}
                                  className="h-8 w-20"
                                />
                                <span className="text-sm text-muted-foreground">p/L</span>
                              </div>
                            ) : (
                              <span className="text-sm">{fc.handling_fee_per_litre?.toFixed(2)} p/L</span>
                            )}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground capitalize">{fc.settlement_frequency}</td>
                          <td className="p-3 text-center">
                            <Switch 
                              checked={fc.is_active} 
                              onCheckedChange={() => toggleFuelCardType(fc.id, fc.is_active)}
                            />
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-2">
                              {editingFuelCardType?.id === fc.id ? (
                                <>
                                  <Button size="sm" onClick={() => updateFuelCardType(fc.id)}>
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingFuelCardType(null)}>
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingFuelCardType(fc)}>
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteFuelCardType(fc.id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Key Fuels Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-green-500">
                  <Fuel className="h-5 w-5" />
                  Key Fuels
                  <span className="text-xs text-muted-foreground font-normal">(Diesel: Cost + Fee | Others: Retail - Merchant Fee - Monthly)</span>
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Card Name</th>
                        <th className="text-left p-3 text-sm font-medium">Diesel Fee</th>
                        <th className="text-left p-3 text-sm font-medium">Other Grades %</th>
                        <th className="text-left p-3 text-sm font-medium">Shop Items %</th>
                        <th className="text-center p-3 text-sm font-medium">Status</th>
                        <th className="text-right p-3 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {fuelCardTypes.filter(fc => fc.category === 'key_fuels').map((fc) => (
                        <tr key={fc.id} className={!fc.is_active ? 'bg-muted/30' : ''}>
                          <td className="p-3">
                            {editingFuelCardType?.id === fc.id ? (
                              <Input 
                                value={editingFuelCardType.name}
                                onChange={(e) => setEditingFuelCardType({...editingFuelCardType, name: e.target.value})}
                                className="h-8"
                              />
                            ) : (
                              <span className="font-medium">{fc.name}</span>
                            )}
                          </td>
                          <td className="p-3">
                            {editingFuelCardType?.id === fc.id ? (
                              <div className="flex items-center gap-1">
                                <Input 
                                  type="number"
                                  step="0.01"
                                  value={editingFuelCardType.diesel_handling_fee_per_litre}
                                  onChange={(e) => setEditingFuelCardType({...editingFuelCardType, diesel_handling_fee_per_litre: parseFloat(e.target.value) || 0})}
                                  className="h-8 w-20"
                                />
                                <span className="text-sm text-muted-foreground">p/L</span>
                              </div>
                            ) : (
                              <span className="text-sm">{fc.diesel_handling_fee_per_litre?.toFixed(2)} p/L</span>
                            )}
                          </td>
                          <td className="p-3">
                            {editingFuelCardType?.id === fc.id ? (
                              <div className="flex items-center gap-1">
                                <Input 
                                  type="number"
                                  step="0.1"
                                  value={editingFuelCardType.other_grades_merchant_fee_percent}
                                  onChange={(e) => setEditingFuelCardType({...editingFuelCardType, other_grades_merchant_fee_percent: parseFloat(e.target.value) || 0})}
                                  className="h-8 w-16"
                                />
                                <span className="text-sm text-muted-foreground">%</span>
                              </div>
                            ) : (
                              <span className="text-sm">{fc.other_grades_merchant_fee_percent}%</span>
                            )}
                          </td>
                          <td className="p-3">
                            {editingFuelCardType?.id === fc.id ? (
                              <div className="flex items-center gap-1">
                                <Input 
                                  type="number"
                                  step="0.1"
                                  value={editingFuelCardType.shop_items_merchant_fee_percent}
                                  onChange={(e) => setEditingFuelCardType({...editingFuelCardType, shop_items_merchant_fee_percent: parseFloat(e.target.value) || 0})}
                                  className="h-8 w-16"
                                />
                                <span className="text-sm text-muted-foreground">%</span>
                              </div>
                            ) : (
                              <span className="text-sm">{fc.shop_items_merchant_fee_percent}%</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <Switch 
                              checked={fc.is_active} 
                              onCheckedChange={() => toggleFuelCardType(fc.id, fc.is_active)}
                            />
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-2">
                              {editingFuelCardType?.id === fc.id ? (
                                <>
                                  <Button size="sm" onClick={() => updateFuelCardType(fc.id)}>
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingFuelCardType(null)}>
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingFuelCardType(fc)}>
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteFuelCardType(fc.id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Add New Fuel Card Type */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Add New Fuel Card Type</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Card Name</Label>
                    <Input 
                      placeholder="e.g. Esso, Total"
                      value={newFuelCardType.name}
                      onChange={(e) => setNewFuelCardType({...newFuelCardType, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select 
                      value={newFuelCardType.category}
                      onValueChange={(value) => setNewFuelCardType({...newFuelCardType, category: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="non_bunkering">Non Bunkering</SelectItem>
                        <SelectItem value="bunkering">Bunkering</SelectItem>
                        <SelectItem value="key_fuels">Key Fuels</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Settlement</Label>
                    <Select 
                      value={newFuelCardType.settlement_frequency}
                      onValueChange={(value) => setNewFuelCardType({...newFuelCardType, settlement_frequency: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={addFuelCardType} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Card Type
                    </Button>
                  </div>
                </div>
                
                {/* Additional fields for Bunkering */}
                {newFuelCardType.category === 'bunkering' && (
                  <div className="mt-4 p-4 bg-orange-500/10 rounded-lg">
                    <Label>Handling Fee (pence per litre)</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={newFuelCardType.handling_fee_per_litre}
                      onChange={(e) => setNewFuelCardType({...newFuelCardType, handling_fee_per_litre: parseFloat(e.target.value) || 0})}
                      className="w-32 mt-1"
                    />
                  </div>
                )}
                
                {/* Additional fields for Key Fuels */}
                {newFuelCardType.category === 'key_fuels' && (
                  <div className="mt-4 p-4 bg-green-500/10 rounded-lg grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Diesel Handling Fee (p/L)</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newFuelCardType.diesel_handling_fee_per_litre}
                        onChange={(e) => setNewFuelCardType({...newFuelCardType, diesel_handling_fee_per_litre: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Other Grades Merchant Fee (%)</Label>
                      <Input 
                        type="number"
                        step="0.1"
                        value={newFuelCardType.other_grades_merchant_fee_percent}
                        onChange={(e) => setNewFuelCardType({...newFuelCardType, other_grades_merchant_fee_percent: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Shop Items Merchant Fee (%)</Label>
                      <Input 
                        type="number"
                        step="0.1"
                        value={newFuelCardType.shop_items_merchant_fee_percent}
                        onChange={(e) => setNewFuelCardType({...newFuelCardType, shop_items_merchant_fee_percent: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Buying Groups */}
        <TabsContent value="buying-groups" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Building className="h-5 w-5 text-primary" />
                Buying Groups
              </CardTitle>
              <CardDescription>
                Manage buying groups (e.g., Nisa, Spar, Costcutter) for consolidated supplier invoices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add New Buying Group Form */}
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="md:col-span-2">
                  <Label>Group Name *</Label>
                  <Input
                    value={newBuyingGroup.name}
                    onChange={(e) => setNewBuyingGroup({ ...newBuyingGroup, name: e.target.value })}
                    placeholder="e.g., Nisa"
                  />
                </div>
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={newBuyingGroup.contact_name}
                    onChange={(e) => setNewBuyingGroup({ ...newBuyingGroup, contact_name: e.target.value })}
                    placeholder="Contact person"
                  />
                </div>
                <div>
                  <Label>Payment Terms</Label>
                  <Input
                    value={newBuyingGroup.payment_terms}
                    onChange={(e) => setNewBuyingGroup({ ...newBuyingGroup, payment_terms: e.target.value })}
                    placeholder="e.g., Weekly"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newBuyingGroup.contact_email}
                    onChange={(e) => setNewBuyingGroup({ ...newBuyingGroup, contact_email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={addBuyingGroup} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Buying Groups List */}
              <div className="border rounded-lg">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium">Name</th>
                      <th className="text-left p-3 font-medium">Contact</th>
                      <th className="text-left p-3 font-medium">Payment Terms</th>
                      <th className="text-center p-3 font-medium">Status</th>
                      <th className="text-right p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buyingGroups.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center p-8 text-muted-foreground">
                          No buying groups configured. Add one above.
                        </td>
                      </tr>
                    ) : (
                      buyingGroups.map((group) => (
                        <tr key={group.id} className="border-b hover:bg-muted/20">
                          {editingBuyingGroup?.id === group.id ? (
                            <>
                              <td className="p-2">
                                <Input
                                  value={editingBuyingGroup.name}
                                  onChange={(e) => setEditingBuyingGroup({ ...editingBuyingGroup, name: e.target.value })}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  value={editingBuyingGroup.contact_name}
                                  onChange={(e) => setEditingBuyingGroup({ ...editingBuyingGroup, contact_name: e.target.value })}
                                  placeholder="Contact name"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  value={editingBuyingGroup.payment_terms}
                                  onChange={(e) => setEditingBuyingGroup({ ...editingBuyingGroup, payment_terms: e.target.value })}
                                />
                              </td>
                              <td className="p-2 text-center">
                                <Switch
                                  checked={editingBuyingGroup.is_active}
                                  onCheckedChange={(checked) => setEditingBuyingGroup({ ...editingBuyingGroup, is_active: checked })}
                                />
                              </td>
                              <td className="p-2 text-right space-x-2">
                                <Button size="sm" onClick={() => updateBuyingGroup(group.id)}>
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingBuyingGroup(null)}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="p-3 font-medium">{group.name}</td>
                              <td className="p-3 text-muted-foreground">
                                {group.contact_name || '-'}
                                {group.contact_email && <span className="block text-xs">{group.contact_email}</span>}
                              </td>
                              <td className="p-3">{group.payment_terms || '-'}</td>
                              <td className="p-3 text-center">
                                <Switch
                                  checked={group.is_active}
                                  onCheckedChange={() => toggleBuyingGroup(group.id, group.is_active)}
                                />
                              </td>
                              <td className="p-3 text-right space-x-2">
                                <Button size="sm" variant="ghost" onClick={() => setEditingBuyingGroup({ ...group })}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteBuyingGroup(group.id)}>
                                  <Trash className="h-4 w-4" />
                                </Button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Types */}
        <TabsContent value="payment-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <CreditCard className="h-5 w-5 text-primary" />
                Payment Types
              </CardTitle>
              <CardDescription>
                Manage payment methods available for stores
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  These payment types will be available for selection when setting up stores and entering sales.
                </p>
              </div>

              <div className="space-y-3">
                {paymentTypes.map((type) => (
                  <div key={type.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={type.is_active}
                        onCheckedChange={() => togglePaymentType(type.id, type.is_active)}
                      />
                      <span className={type.is_active ? '' : 'text-muted-foreground'}>{type.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deletePaymentType(type.id)}
                      className="text-destructive"
                      data-testid={`delete-payment-${type.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Input
                  placeholder="New payment type (e.g., Bank Transfer)"
                  value={newPaymentType}
                  onChange={(e) => setNewPaymentType(e.target.value)}
                  className="max-w-xs"
                  data-testid="new-payment-type-input"
                />
                <Button variant="outline" onClick={addPaymentType} data-testid="add-payment-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Payment Type
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Paid Out Types */}
        <TabsContent value="paid-out-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Receipt className="h-5 w-5 text-primary" />
                Paid Out Types
              </CardTitle>
              <CardDescription>
                Manage categories for recording paid out expenses
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  These categories will be available when staff record paid out expenses during sales entry.
                </p>
              </div>

              <div className="space-y-3">
                {paidOutTypes.map((type) => (
                  <div key={type.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={type.is_active}
                        onCheckedChange={() => togglePaidOutType(type.id, type.is_active)}
                      />
                      <span className={type.is_active ? '' : 'text-muted-foreground'}>{type.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deletePaidOutType(type.id)}
                      className="text-destructive"
                      data-testid={`delete-paidout-${type.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Input
                  placeholder="New paid out type (e.g., Cleaning Supplies)"
                  value={newPaidOutType}
                  onChange={(e) => setNewPaidOutType(e.target.value)}
                  className="max-w-xs"
                  data-testid="new-paidout-type-input"
                />
                <Button variant="outline" onClick={addPaidOutType} data-testid="add-paidout-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Paid Out Type
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Purchase Types Tab */}
        <TabsContent value="purchase-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <ShoppingBag className="h-5 w-5 text-primary" />
                Purchase Types
              </CardTitle>
              <CardDescription>
                Manage categories for purchase invoices (Shop, Fuel, Maintenance, etc.) to track margins by department
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  These purchase types help categorize expenses and calculate department-specific margins in reports.
                </p>
              </div>

              <div className="space-y-3">
                {purchaseTypes.map((type) => (
                  <div key={type.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    {editingPurchaseType?.id === type.id ? (
                      <div className="flex items-center gap-3 flex-1 mr-4">
                        <Switch
                          checked={editingPurchaseType.is_active}
                          onCheckedChange={(checked) => setEditingPurchaseType({ ...editingPurchaseType, is_active: checked })}
                        />
                        <Input
                          value={editingPurchaseType.name}
                          onChange={(e) => setEditingPurchaseType({ ...editingPurchaseType, name: e.target.value })}
                          className="flex-1 max-w-[200px]"
                          data-testid={`edit-purchase-type-name-${type.id}`}
                        />
                        <Input
                          value={editingPurchaseType.description || ''}
                          onChange={(e) => setEditingPurchaseType({ ...editingPurchaseType, description: e.target.value })}
                          placeholder="Description"
                          className="flex-1"
                          data-testid={`edit-purchase-type-desc-${type.id}`}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={type.is_active}
                          onCheckedChange={() => togglePurchaseType(type.id, type.is_active)}
                        />
                        <span className={type.is_active ? '' : 'text-muted-foreground'}>{type.name}</span>
                        {type.description && (
                          <span className="text-xs text-muted-foreground">({type.description})</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      {editingPurchaseType?.id === type.id ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updatePurchaseType(type.id)}
                            className="text-green-500"
                            data-testid={`save-purchase-type-${type.id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingPurchaseType(null)}
                            className="text-muted-foreground"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingPurchaseType({ ...type })}
                            data-testid={`edit-purchase-type-${type.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deletePurchaseType(type.id)}
                            className="text-destructive"
                            data-testid={`delete-purchase-type-${type.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 items-end flex-wrap">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="e.g., Equipment"
                    value={newPurchaseType.name}
                    onChange={(e) => setNewPurchaseType({ ...newPurchaseType, name: e.target.value })}
                    className="w-48"
                    data-testid="new-purchase-type-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Input
                    placeholder="e.g., Store equipment and fixtures"
                    value={newPurchaseType.description}
                    onChange={(e) => setNewPurchaseType({ ...newPurchaseType, description: e.target.value })}
                    className="w-64"
                    data-testid="new-purchase-type-desc"
                  />
                </div>
                <Button variant="outline" onClick={addPurchaseType} data-testid="add-purchase-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Purchase Type
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commission Types Tab */}
        <TabsContent value="commission-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <BadgePoundSterling className="h-5 w-5 text-primary" />
                Commission Types
              </CardTitle>
              <CardDescription>
                Manage commission income categories (Lottery, PayPoint, Western Union, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  These commission types will appear in the Commission Income page for recording commission earnings.
                </p>
              </div>

              <div className="space-y-3">
                {commissionTypes.map((type) => (
                  <div key={type.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    {editingCommissionType?.id === type.id ? (
                      <div className="flex items-center gap-3 flex-1 mr-4">
                        <Switch
                          checked={editingCommissionType.is_active}
                          onCheckedChange={(checked) => setEditingCommissionType({ ...editingCommissionType, is_active: checked })}
                        />
                        <Input
                          value={editingCommissionType.name}
                          onChange={(e) => setEditingCommissionType({ ...editingCommissionType, name: e.target.value })}
                          className="flex-1 max-w-[200px]"
                          data-testid={`edit-commission-type-name-${type.id}`}
                        />
                        <Input
                          value={editingCommissionType.description || ''}
                          onChange={(e) => setEditingCommissionType({ ...editingCommissionType, description: e.target.value })}
                          placeholder="Description"
                          className="flex-1"
                          data-testid={`edit-commission-type-desc-${type.id}`}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={type.is_active}
                          onCheckedChange={() => toggleCommissionType(type.id, type.is_active)}
                        />
                        <span className={type.is_active ? '' : 'text-muted-foreground'}>{type.name}</span>
                        {type.description && (
                          <span className="text-xs text-muted-foreground">({type.description})</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      {editingCommissionType?.id === type.id ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateCommissionType(type.id)}
                            className="text-green-500"
                            data-testid={`save-commission-type-${type.id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingCommissionType(null)}
                            className="text-muted-foreground"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingCommissionType({ ...type })}
                            data-testid={`edit-commission-type-${type.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteCommissionType(type.id)}
                            className="text-destructive"
                            data-testid={`delete-commission-type-${type.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 items-end flex-wrap">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="e.g., DHL Commission"
                    value={newCommissionType.name}
                    onChange={(e) => setNewCommissionType({ ...newCommissionType, name: e.target.value })}
                    className="w-48"
                    data-testid="new-commission-type-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Input
                    placeholder="e.g., Parcel service commission"
                    value={newCommissionType.description}
                    onChange={(e) => setNewCommissionType({ ...newCommissionType, description: e.target.value })}
                    className="w-64"
                    data-testid="new-commission-type-desc"
                  />
                </div>
                <Button variant="outline" onClick={addCommissionType} data-testid="add-commission-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Commission Type
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expense Types Tab */}
        <TabsContent value="expense-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Receipt className="h-5 w-5 text-primary" />
                Expense Types
              </CardTitle>
              <CardDescription>
                Manage expense categories for business expenditures (Wages, PAYE, VAT, Utilities, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  These expense types will appear in the Expenses page for recording business expenditures.
                </p>
                {expenseTypes.length === 0 && (
                  <Button variant="outline" size="sm" onClick={seedDefaultExpenseTypes} data-testid="seed-expense-types-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Seed Default Types
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {expenseTypes.map((type) => (
                  <div key={type.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    {editingExpenseType?.id === type.id ? (
                      <div className="flex items-center gap-3 flex-1 mr-4">
                        <Switch
                          checked={editingExpenseType.is_active !== false}
                          onCheckedChange={(checked) => setEditingExpenseType({ ...editingExpenseType, is_active: checked })}
                        />
                        <Input
                          value={editingExpenseType.name}
                          onChange={(e) => setEditingExpenseType({ ...editingExpenseType, name: e.target.value })}
                          className="flex-1 max-w-[200px]"
                          data-testid={`edit-expense-type-name-${type.id}`}
                        />
                        <Input
                          value={editingExpenseType.description || ''}
                          onChange={(e) => setEditingExpenseType({ ...editingExpenseType, description: e.target.value })}
                          placeholder="Description"
                          className="flex-1"
                          data-testid={`edit-expense-type-desc-${type.id}`}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={type.is_active !== false}
                          onCheckedChange={() => toggleExpenseType(type.id, type.is_active !== false)}
                        />
                        <span className={type.is_active !== false ? '' : 'text-muted-foreground'}>{type.name}</span>
                        {type.description && (
                          <span className="text-xs text-muted-foreground">({type.description})</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      {editingExpenseType?.id === type.id ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateExpenseType(type.id)}
                            className="text-green-500"
                            data-testid={`save-expense-type-${type.id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingExpenseType(null)}
                            className="text-muted-foreground"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingExpenseType({ ...type })}
                            data-testid={`edit-expense-type-${type.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteExpenseType(type.id)}
                            className="text-destructive"
                            data-testid={`delete-expense-type-${type.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 items-end flex-wrap">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="e.g., Staff Wages"
                    value={newExpenseType.name}
                    onChange={(e) => setNewExpenseType({ ...newExpenseType, name: e.target.value })}
                    className="w-48"
                    data-testid="new-expense-type-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Input
                    placeholder="e.g., Employee salary payments"
                    value={newExpenseType.description}
                    onChange={(e) => setNewExpenseType({ ...newExpenseType, description: e.target.value })}
                    className="w-64"
                    data-testid="new-expense-type-desc"
                  />
                </div>
                <Button variant="outline" onClick={addExpenseType} data-testid="add-expense-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Expense Type
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cashiers Tab */}
        <TabsContent value="cashiers" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Users className="h-5 w-5 text-primary" />
                Cashiers / Staff
              </CardTitle>
              <CardDescription>
                Manage staff details for each store. Staff records are stored separately per store.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Store Selector */}
              <div className="mb-6 p-4 bg-muted/30 rounded-lg">
                <Label className="mb-2 block">Select Store</Label>
                <Select value={cashierStoreId} onValueChange={setCashierStoreId}>
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Select a store to manage staff" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>
                        {store.name} ({store.store_type === 'fuel_station' ? 'Fuel Station' : 'Convenience Store'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {!cashierStoreId ? (
                <div className="text-center py-8 text-muted-foreground">
                  Please select a store to manage staff
                </div>
              ) : (
                <>
              {/* Cashiers Table */}
              <div className="border rounded-lg overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2 font-medium w-10">Active</th>
                      <th className="text-left p-2 font-medium">ID</th>
                      <th className="text-left p-2 font-medium">First Name</th>
                      <th className="text-left p-2 font-medium">Last Name</th>
                      <th className="text-left p-2 font-medium">Pay Type</th>
                      <th className="text-left p-2 font-medium">Rate/Salary</th>
                      <th className="text-left p-2 font-medium">Contracted Hrs</th>
                      <th className="text-left p-2 font-medium">Mobile</th>
                      <th className="text-left p-2 font-medium">Join Date</th>
                      <th className="text-center p-2 font-medium w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashiers.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center p-6 text-muted-foreground">
                          No staff found for this store. Add one below.
                        </td>
                      </tr>
                    ) : (
                    cashiers.map(cashier => (
                      <tr 
                        key={cashier.id} 
                        className={`border-t ${!cashier.is_active ? 'bg-muted/50 opacity-60' : ''}`}
                      >
                        <td className="p-2">
                          <Switch
                            checked={cashier.is_active}
                            onCheckedChange={() => toggleCashier(cashier.id, cashier.is_active)}
                          />
                        </td>
                        <td className="p-2">
                          {editingCashier?.id === cashier.id ? (
                            <Input
                              value={editingCashier.cashier_id || ''}
                              onChange={(e) => setEditingCashier({ ...editingCashier, cashier_id: e.target.value })}
                              className="w-16 h-8 text-xs"
                              placeholder="ID"
                            />
                          ) : (
                            <span className="text-xs font-mono">{cashier.cashier_id || '-'}</span>
                          )}
                        </td>
                        <td className="p-2">
                          {editingCashier?.id === cashier.id ? (
                            <Input
                              value={editingCashier.first_name || editingCashier.name || ''}
                              onChange={(e) => setEditingCashier({ ...editingCashier, first_name: e.target.value })}
                              className="w-24 h-8 text-xs"
                            />
                          ) : (
                            <span className={!cashier.is_active ? 'line-through' : 'font-medium'}>
                              {cashier.first_name || cashier.name || '-'}
                            </span>
                          )}
                        </td>
                        <td className="p-2">
                          {editingCashier?.id === cashier.id ? (
                            <Input
                              value={editingCashier.last_name || ''}
                              onChange={(e) => setEditingCashier({ ...editingCashier, last_name: e.target.value })}
                              className="w-24 h-8 text-xs"
                            />
                          ) : (
                            <span>{cashier.last_name || '-'}</span>
                          )}
                        </td>
                        <td className="p-2">
                          {editingCashier?.id === cashier.id ? (
                            <Select
                              value={editingCashier.pay_type || 'hourly'}
                              onValueChange={(value) => setEditingCashier({ ...editingCashier, pay_type: value })}
                            >
                              <SelectTrigger className="w-24 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="hourly">Hourly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`text-xs px-2 py-1 rounded ${cashier.pay_type === 'monthly' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                              {cashier.pay_type === 'monthly' ? 'Monthly' : 'Hourly'}
                            </span>
                          )}
                        </td>
                        <td className="p-2">
                          {editingCashier?.id === cashier.id ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={editingCashier.pay_type === 'monthly' 
                                ? (editingCashier.monthly_salary || 0) 
                                : (editingCashier.hourly_rate || 0)}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                if (editingCashier.pay_type === 'monthly') {
                                  setEditingCashier({ ...editingCashier, monthly_salary: val });
                                } else {
                                  setEditingCashier({ ...editingCashier, hourly_rate: val });
                                }
                              }}
                              className="w-20 h-8 text-xs"
                              placeholder={editingCashier.pay_type === 'monthly' ? '£/month' : '£/hour'}
                            />
                          ) : (
                            <span className="text-xs font-mono">
                              {cashier.pay_type === 'monthly' 
                                ? `£${(cashier.monthly_salary || 0).toFixed(2)}/mo`
                                : `£${(cashier.hourly_rate || 0).toFixed(2)}/hr`}
                            </span>
                          )}
                        </td>
                        <td className="p-2">
                          {editingCashier?.id === cashier.id ? (
                            <Input
                              type="number"
                              value={editingCashier.contracted_hours || 0}
                              onChange={(e) => setEditingCashier({ ...editingCashier, contracted_hours: parseFloat(e.target.value) || 0 })}
                              className="w-16 h-8 text-xs"
                            />
                          ) : (
                            <span className="text-xs font-mono">{cashier.contracted_hours || 0}</span>
                          )}
                        </td>
                        <td className="p-2">
                          {editingCashier?.id === cashier.id ? (
                            <Input
                              value={editingCashier.mobile || ''}
                              onChange={(e) => setEditingCashier({ ...editingCashier, mobile: e.target.value })}
                              className="w-24 h-8 text-xs"
                            />
                          ) : (
                            <span className="text-xs">{cashier.mobile || '-'}</span>
                          )}
                        </td>
                        <td className="p-2">
                          {editingCashier?.id === cashier.id ? (
                            <Input
                              type="date"
                              value={editingCashier.join_date || ''}
                              onChange={(e) => setEditingCashier({ ...editingCashier, join_date: e.target.value })}
                              className="w-32 h-8 text-xs"
                            />
                          ) : (
                            <span className="text-xs">{cashier.join_date || '-'}</span>
                          )}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center justify-center gap-1">
                            {editingCashier?.id === cashier.id ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => updateCashier(cashier.id)}
                                  className="text-green-500 h-7 w-7"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setEditingCashier(null)}
                                  className="text-muted-foreground h-7 w-7"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setEditingCashier({ 
                                    ...cashier, 
                                    // Migrate legacy 'name' to 'first_name' for editing
                                    first_name: cashier.first_name || cashier.name || '' 
                                  })}
                                  className="h-7 w-7"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => deleteCashier(cashier.id)}
                                  className="text-destructive h-7 w-7"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Add New Cashier Form */}
              <div className="border rounded-lg p-4 bg-muted/20">
                <h4 className="font-medium mb-4">Add New Staff Member</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Cashier ID (Short Name)</Label>
                    <Input
                      placeholder="e.g., JD01"
                      value={newCashier.cashier_id}
                      onChange={(e) => setNewCashier({ ...newCashier, cashier_id: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">First Name *</Label>
                    <Input
                      placeholder="First Name"
                      value={newCashier.first_name}
                      onChange={(e) => setNewCashier({ ...newCashier, first_name: e.target.value })}
                      className="h-8 text-sm"
                      data-testid="new-cashier-first-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Last Name</Label>
                    <Input
                      placeholder="Last Name"
                      value={newCashier.last_name}
                      onChange={(e) => setNewCashier({ ...newCashier, last_name: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Pay Type</Label>
                    <Select
                      value={newCashier.pay_type || 'hourly'}
                      onValueChange={(value) => setNewCashier({ ...newCashier, pay_type: value })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {(newCashier.pay_type || 'hourly') === 'monthly' ? 'Monthly Salary (£)' : 'Hourly Rate (£)'}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={(newCashier.pay_type || 'hourly') === 'monthly' 
                        ? (newCashier.monthly_salary || '') 
                        : (newCashier.hourly_rate || '')}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        if ((newCashier.pay_type || 'hourly') === 'monthly') {
                          setNewCashier({ ...newCashier, monthly_salary: val });
                        } else {
                          setNewCashier({ ...newCashier, hourly_rate: val });
                        }
                      }}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Contracted Hours</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={newCashier.contracted_hours || ''}
                      onChange={(e) => setNewCashier({ ...newCashier, contracted_hours: parseFloat(e.target.value) || 0 })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Mobile</Label>
                    <Input
                      placeholder="Phone Number"
                      value={newCashier.mobile}
                      onChange={(e) => setNewCashier({ ...newCashier, mobile: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Join Date</Label>
                    <Input
                      type="date"
                      value={newCashier.join_date}
                      onChange={(e) => setNewCashier({ ...newCashier, join_date: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Button variant="outline" onClick={addCashier} data-testid="add-cashier-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Staff
                  </Button>
                </div>
              </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scratch Card Denominations Tab */}
        <TabsContent value="scratch-cards" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Scratch Card Denominations
              </CardTitle>
              <CardDescription>
                Manage scratch card denominations for each store. Select a store to view and edit its denominations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Store Selector */}
              <div className="flex items-center gap-4">
                <Label className="whitespace-nowrap">Select Store:</Label>
                <Select value={scratchCardStoreId} onValueChange={setScratchCardStoreId}>
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Select a store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>
                        {store.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!scratchCardStoreId ? (
                <p className="text-muted-foreground text-center py-8">Please select a store to manage its scratch card denominations</p>
              ) : (
                <>
                  {scratchCardDenominations.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-center p-3 text-sm font-medium w-20">Active</th>
                            <th className="text-left p-3 text-sm font-medium">Name</th>
                            <th className="text-center p-3 text-sm font-medium w-32">Value (£)</th>
                            <th className="text-center p-3 text-sm font-medium w-32">Pack Size</th>
                            <th className="text-center p-3 text-sm font-medium w-20">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {[...scratchCardDenominations].sort((a, b) => (a.value || 0) - (b.value || 0)).map((denom) => (
                            <tr key={denom.id} className={`hover:bg-muted/30 ${!denom.is_active ? 'opacity-50' : ''}`}>
                              <td className="p-2 text-center">
                                <Switch
                                  checked={denom.is_active}
                                  onCheckedChange={() => toggleScratchCardDenomination(denom.id, denom.is_active)}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  value={denom.name}
                                  onChange={(e) => {
                                    setScratchCardDenominations(prev => 
                                      prev.map(d => d.id === denom.id ? {...d, name: e.target.value} : d)
                                    );
                                  }}
                                  onBlur={(e) => updateScratchCardDenomination(denom.id, 'name', e.target.value)}
                                  className="h-9"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={denom.value}
                                  onChange={(e) => {
                                    setScratchCardDenominations(prev => 
                                      prev.map(d => d.id === denom.id ? {...d, value: parseFloat(e.target.value) || 0} : d)
                                    );
                                  }}
                                  onBlur={(e) => updateScratchCardDenomination(denom.id, 'value', e.target.value)}
                              className="h-9 text-center font-mono"
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              value={denom.pack_size || 100}
                              onChange={(e) => {
                                setScratchCardDenominations(prev => 
                                  prev.map(d => d.id === denom.id ? {...d, pack_size: parseInt(e.target.value) || 100} : d)
                                );
                              }}
                              onBlur={(e) => updateScratchCardDenomination(denom.id, 'pack_size', e.target.value)}
                              className="h-9 text-center font-mono"
                            />
                          </td>
                          <td className="p-2 text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteScratchCardDenomination(denom.id)}
                              className="text-destructive h-9 w-9"
                              data-testid={`delete-denom-${denom.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex gap-4 items-end flex-wrap border-t pt-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="e.g., £20 Cards"
                    value={newDenomination.name}
                    onChange={(e) => setNewDenomination({ ...newDenomination, name: e.target.value })}
                    className="w-40"
                    data-testid="new-denom-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Value (£)</Label>
                  <Input
                    type="number"
                    placeholder="20"
                    value={newDenomination.value}
                    onChange={(e) => setNewDenomination({ ...newDenomination, value: e.target.value })}
                    className="w-24"
                    data-testid="new-denom-value-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pack Size</Label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={newDenomination.pack_size || ''}
                    onChange={(e) => setNewDenomination({ ...newDenomination, pack_size: e.target.value })}
                    className="w-24"
                    data-testid="new-denom-pack-input"
                  />
                </div>
                <Button variant="outline" onClick={addScratchCardDenomination} data-testid="add-denom-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Denomination
                </Button>
              </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transfer Types Tab */}
        <TabsContent value="transfer-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Money Transfer Types
              </CardTitle>
              <CardDescription>
                Manage types of money transfers for cash movement tracking (Bank Deposit, Supplier Payment, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                {transferTypes.map((type) => (
                  <div
                    key={type.id}
                    className="flex items-center justify-between p-3 border border-border rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={type.is_active}
                        onCheckedChange={() => toggleTransferType(type.id, type.is_active)}
                      />
                      <span className={type.is_active ? '' : 'text-muted-foreground'}>{type.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteTransferType(type.id)}
                      className="text-destructive"
                      data-testid={`delete-transfer-${type.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Input
                  placeholder="New transfer type (e.g., Petty Cash)"
                  value={newTransferType}
                  onChange={(e) => setNewTransferType(e.target.value)}
                  className="max-w-xs"
                  data-testid="new-transfer-type-input"
                />
                <Button variant="outline" onClick={addTransferType} data-testid="add-transfer-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Transfer Type
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
